import React, { useEffect, useState, useCallback } from "react";
import { Plus, Search, RefreshCw, Filter, X, Box, User, Info, Ticket as TicketIcon, ChevronRight, Trash2, Loader2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { TicketStatusBadge, TICKET_STATUS_COLORS } from "@/components/TicketStatusBadge";
import { TableSkeleton } from "@/components/Skeleton";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import CreateTicketModal from "@/components/tickets/CreateTicketModal";
import TicketDetailPanel from "@/components/tickets/TicketDetailPanel";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

const selectClass = "h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export default function TicketsListPage() {
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const isAdmin = user?.role === "admin";
  const canCreateTicket = user?.role !== "warehouse_staff";
  const [data, setData] = useState({ items: [], total: 0, overview: {}, overview_total: 0 });
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [includeCompleted, setIncludeCompleted] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_completed: includeCompleted };
      if (search) params.q = search;
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      const [t, m] = await Promise.all([
        api.get("/tickets", { params }),
        meta ? Promise.resolve({ data: meta }) : api.get("/tickets/meta"),
      ]);
      setData(t.data);
      if (!meta) setMeta(m.data);
    } finally { setLoading(false); }
  }, [search, statusFilter, typeFilter, includeCompleted]);

  useEffect(() => { load(); }, [load]);

  // debounced search
  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
  }, [search]);

  const overviewData = Object.entries(data.overview || {})
    .map(([name, value]) => ({ name, value }))
    .filter((d) => d.value > 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Tickets</h2>
          <p className="text-muted-foreground">Support tickets &amp; returns workflow</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="tickets-refresh-btn"><RefreshCw className="h-4 w-4" /> Refresh</Button>
          {canCreateTicket && (
            <Button onClick={() => setCreateOpen(true)} data-testid="new-ticket-btn"><Plus className="h-4 w-4" /> New Ticket</Button>
          )}
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* LEFT — list */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by ticket code…" className="pl-9" />
            </div>
            <Button variant="outline" size="sm" onClick={() => setShowFilters((s) => !s)}>
              <Filter className="h-4 w-4" /> Filters
            </Button>
          </div>

          {showFilters && meta && (
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-white p-3">
              <select className={selectClass} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">All statuses</option>
                {meta.statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <select className={selectClass} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All types</option>
                {meta.types.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input type="checkbox" checked={includeCompleted} onChange={(e) => setIncludeCompleted(e.target.checked)} />
                Include completed
              </label>
              {(statusFilter || typeFilter) && (
                <Button variant="ghost" size="sm" onClick={() => { setStatusFilter(""); setTypeFilter(""); }}><X className="h-3.5 w-3.5" /> Clear</Button>
              )}
            </div>
          )}

          {!includeCompleted && !statusFilter && (
            <p className="flex items-center gap-1.5 text-xs text-slate-500"><Filter className="h-3 w-3" /> Showing active tickets (excluding completed) by default</p>
          )}

          {loading ? <TableSkeleton rows={4} cols={4} /> : data.items.length === 0 ? (
            <Card><CardContent className="flex flex-col items-center gap-2 p-12 text-center text-muted-foreground">
              <TicketIcon className="h-6 w-6" />
              <p>No tickets match your filters.</p>
            </CardContent></Card>
          ) : (
            <div className="space-y-3">
              {data.items.map((t) => <TicketCard key={t.id} ticket={t} isAdmin={isAdmin} onOpen={() => setDetailId(t.id)} onDeleted={load} />)}
            </div>
          )}
        </div>

        {/* RIGHT — overview */}
        <div className="space-y-4">
          <Card className="sticky top-20">
            <CardHeader><CardTitle className="text-base">Ticket Status Overview</CardTitle></CardHeader>
            <CardContent>
              {overviewData.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">No tickets match your filters.</p>
              ) : (
                <>
                  <div className="relative">
                    <ResponsiveContainer width="100%" height={180} minWidth={120} minHeight={120} initialDimension={{ width: 220, height: 180 }}>
                      <PieChart>
                        <Pie data={overviewData} dataKey="value" nameKey="name" innerRadius={55} outerRadius={75} paddingAngle={2} stroke="none" isAnimationActive={false}>
                          {overviewData.map((d) => <Cell key={d.name} fill={TICKET_STATUS_COLORS[d.name] || "#94a3b8"} />)}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                    <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                      <p className="text-3xl font-bold leading-none tracking-tight">{data.overview_total}</p>
                      <p className="mt-1 text-xs text-slate-500">Tickets</p>
                    </div>
                  </div>
                  <p className="mt-2 text-center text-xs text-slate-500">Click legend items to toggle status filters</p>
                  <div className="mt-3 space-y-1.5">
                    {overviewData.map((d) => {
                      const pct = data.overview_total ? Math.round((d.value / data.overview_total) * 100) : 0;
                      const active = statusFilter === d.name;
                      return (
                        <button
                          key={d.name}
                          onClick={() => setStatusFilter(active ? "" : d.name)}
                          className={cn("flex w-full items-center justify-between gap-2 rounded-md border px-3 py-1.5 text-xs transition", active ? "border-slate-400 bg-slate-100" : "border-slate-200 bg-white hover:bg-slate-50")}
                        >
                          <span className="flex items-center gap-2">
                            <span className="h-2 w-2 rounded-full" style={{ background: TICKET_STATUS_COLORS[d.name] }} />
                            <span className="font-medium text-slate-700">{d.name}</span>
                          </span>
                          <span className="text-slate-500">{d.value} <span className="text-slate-400">({pct}%)</span></span>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <CreateTicketModal open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(t) => { load(); setDetailId(t.id); }} />
      <TicketDetailPanel ticketId={detailId} open={!!detailId} onClose={() => setDetailId(null)} onUpdated={load} />
    </div>
  );
}

function TicketCard({ ticket, isAdmin, onOpen, onDeleted }) {
  const latest = ticket.latest_note;
  const [deleting, setDeleting] = useState(false);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState(null);

  async function confirmDelete() {
    setError(null);
    setDeleting(true);
    try {
      await api.delete(`/tickets/${ticket.id}`);
      setOpen(false);
      onDeleted && onDeleted();
    } catch (e) {
      setError(apiError(e));
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="group relative overflow-hidden rounded-xl border border-slate-200 bg-white transition hover:border-slate-300 hover:shadow-sm card-lift">
      <button onClick={onOpen} className="block w-full text-left">
        <div className="flex items-start justify-between gap-3 p-5">
          <div className="min-w-0 flex-1 space-y-3">
            <div>
              <p className="font-bold text-slate-900">{ticket.ticket_code} — {ticket.ticket_type}</p>
              <p className="text-xs text-slate-500">{new Date(ticket.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p>
            </div>
            <div className="flex items-start gap-2 text-sm text-slate-700">
              <Box className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <div className="min-w-0">
                <p className="font-medium">{[ticket.product_type, ticket.asset_id, ticket.serial_number && `(SN: ${ticket.serial_number})`].filter(Boolean).join(" · ")}</p>
                {ticket.ticket_info && <p className="text-xs text-slate-500">{ticket.ticket_info}</p>}
              </div>
            </div>
            <div className="flex items-start gap-2 text-sm text-slate-700">
              <User className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
              <p>Pending with: <span className="font-semibold">{(ticket.pending_with || {}).name || "Unassigned"}</span></p>
            </div>
            {latest && (
              <div className="flex items-start gap-2 text-sm text-slate-700">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                <div className="min-w-0">
                  <p>Latest note: <span className="font-semibold">{latest.text.split("\n")[0]}</span></p>
                  <p className="text-xs text-slate-500">{new Date(latest.created_at).toLocaleString("en-US", { dateStyle: "long", timeStyle: "short" })}</p>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            <TicketStatusBadge status={ticket.status} />
          </div>
        </div>
        <div className="flex items-center justify-between border-t border-slate-100 bg-slate-50/50 px-5 py-2.5 text-xs">
          <span className="font-medium text-sky-700">View Notes ({(ticket.attachment_count || 0)} attachments)</span>
          <ChevronRight className="h-4 w-4 text-slate-400" />
        </div>
      </button>
      {/* Delete is available to anyone who can see the ticket (server enforces org-scoped visibility). */}
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setError(null); }}>
          <AlertDialogTrigger asChild>
            <button
              onClick={(e) => { e.stopPropagation(); setOpen(true); }}
              className="absolute right-3 top-3 rounded-md border border-slate-200 bg-white p-1.5 text-slate-400 opacity-0 transition hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 group-hover:opacity-100"
              title="Delete ticket"
              aria-label="Delete ticket"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this ticket?</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="block">You are about to permanently delete <span className="font-semibold text-slate-900">{ticket.ticket_code}</span> — {ticket.ticket_type}.</span>
                <span className="mt-2 block text-rose-600">This will also remove all of its notes and attachments. This action cannot be undone.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            {error && <p className="rounded-md bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={deleting}
                onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                className="bg-rose-600 text-white hover:bg-rose-700"
              >
                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                Yes, delete ticket
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
    </div>
  );
}
